import { Badge } from "@/components/ui/badge";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

import { captureStatusBody } from "../conversation/capture-status";
import { reasonLabelFor, stateLabel, type Tone } from "../../lib/labels";

type BadgeVariant = "success" | "warning" | "info" | "outline-muted";

/**
 * Tone → jobs-list badge recipe. `outline-muted` is the neutral/terminal pill
 * the jobs table uses for "Closed"; danger reuses it with a destructive tint
 * because Badge's solid `destructive` variant reads as an error banner.
 */
const TONE: Record<Tone, { variant: BadgeVariant; className?: string }> = {
  neutral: {
    variant: "outline-muted",
    className: "border-border bg-contrast/4 text-contrast/50",
  },
  info: { variant: "info", className: "border-border" },
  success: { variant: "success", className: "border-border" },
  warning: { variant: "warning", className: "border-border" },
  danger: {
    variant: "outline-muted",
    className:
      "border-destructive/20 bg-destructive/10 text-destructive dark:text-destructive",
  },
};

export function StateChip({
  state,
  stateReason,
  size = "xs",
  className,
}: {
  state: string;
  stateReason?: string | null;
  size?: "xs" | "sm";
  className?: string;
}) {
  const meta = stateLabel(state);
  const tone = TONE[meta.tone];
  const spinning = meta.icon === "IconLoader";
  const reasonTitle = stateReason
    ? reasonLabelFor(state, stateReason)
    : captureStatusBody(state, null) ?? undefined;
  return (
    <Badge
      variant={tone.variant}
      size={size}
      className={cn("rounded-md leading-4", tone.className, className)}
      title={reasonTitle}
    >
      <Icon
        name={meta.icon}
        size={11}
        aria-hidden
        className={cn(spinning && "animate-spin")}
      />
      <span>{meta.label}</span>
    </Badge>
  );
}
